'use client';
/**
 * The "you are connected as <someone else>" strip, shared by the kit shell
 * (MinthrAppShell) and the legacy Header. Its `tw-h-6` height is the same
 * 30-unit offset the legacy Header / Sidenav use (`tw-top-6`).
 */

import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Button } from '@minthr-saas/ui-kit';
import { UserCog } from 'lucide-react';
import { DeconnectFromUser } from 'features/auth/slices/authSlice';
import { syncLanguage } from 'features/auth/helpers';
import store, { AppDispatch } from 'store';

const ImpersonationBanner: React.FC<{
  fullname?: string | null;
  className?: string;
}> = ({ fullname, className }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  // Session is gone server-side — drop the local one and reload to the login
  // screen rather than leaving the user stuck in a half-impersonated state.
  const handleForceLogout = useCallback(async () => {
    toast.error(
      t('Error.SessionExpired') || 'Session expired. Please login again.',
    );
    store.dispatch({ type: 'auth/logout' });
    window.location.reload();
  }, [t]);

  const handleDisconnect = useCallback(async () => {
    const resultAction = await dispatch(DeconnectFromUser());

    if (DeconnectFromUser.fulfilled.match(resultAction)) {
      if (resultAction.payload.culture) {
        await syncLanguage(dispatch, resultAction.payload.culture);
      }
      // Full reload, same as legacy: every cached page/query belongs to the
      // impersonated user and must not survive the switch back.
      window.location.reload();
      return;
    }

    if (DeconnectFromUser.rejected.match(resultAction)) {
      const payload = resultAction.payload;
      if (payload?.status === 401) {
        await handleForceLogout();
        return;
      }
      const errorMessage = payload?.data.message || t('Error.Unknown');
      toast.error(
        <div>
          <div>{errorMessage}</div>
          <div className="tw-flex tw-justify-end tw-mt-[8px]">
            <Button variant="danger" size="sm" onClick={handleForceLogout}>
              {t('Error.ForceLogout') || 'Force Logout'}
            </Button>
          </div>
        </div>,
        { autoClose: false, closeOnClick: false },
      );
    }
  }, [dispatch, t, handleForceLogout]);

  return (
    <div
      role="status"
      className={clsx(
        'tw-flex tw-h-6 tw-shrink-0 tw-items-center tw-justify-center tw-gap-[8px] tw-px-[16px] tw-bg-[var(--minthr-color-secondary)] tw-text-[12px] tw-text-gray-800',
        className,
      )}
    >
      <UserCog
        size={13}
        strokeWidth={1.8}
        className="tw-text-brand-700 tw-shrink-0"
      />
      <span className="tw-truncate">
        {t('Layout.urConnectedTo')} <strong>{fullname}</strong>
      </span>
      <Button variant="link" size="sm" onClick={handleDisconnect}>
        {t('LogOut')}
      </Button>
    </div>
  );
};

export default ImpersonationBanner;
